import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import { Box, Skeleton } from '@mui/material';
import { useLogo } from '../../services/logoService';

/**
 * BrandedLogo – fetches a logo from Firebase and displays it.
 *
 * Usage:
 *   <BrandedLogo  name="logo1-big.png" variant="big" sx={{ height: 55, maxWidth: 180 }} />
 *   <BrandedLogo name="logo1-big.png" hideLoader />  // parent owns the loader
 */
const BrandedLogo = ({ name, variant = 'big', sx = {}, hideLoader = false, onLoad: onLoadProp, ...props }) => {
    const { url, loading, error } = useLogo(name);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [imgError, setImgError] = useState(false);

    // Reset when the logo name changes
    useEffect(() => {
        setImgLoaded(false);
        setImgError(false);
    }, [name]);

    // Callback ref: fires when the <img> DOM node mounts.
    // If the image is already cached by the browser, .complete is true
    // immediately — onLoad may never fire, so we handle it here.
    const imgRefCallback = useCallback((node) => {
        if (node) {
            if (node.complete && node.naturalWidth > 0) {
                setImgLoaded(true);
                onLoadProp?.();  // notify parent too
            }
        }
    }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

    // If logo fetch is done and there is no url / error / image load failed, render nothing
    if (!loading && (!url || error || imgError)) {
        return null;
    }

    const isBig = variant === 'big';
    const defaultWidth = isBig ? 100 : 30;
    const defaultHeight = isBig ? 50 : 30;

    // Pull dimension keys out; everything else goes on the wrapper
    const { width, height, maxWidth, maxHeight, ...wrapperSx } = sx;

    // For the skeleton we need concrete pixel values
    let skelW = typeof width === 'number' ? width : defaultWidth;
    const skelH = typeof height === 'number' ? height : defaultHeight;
    if (typeof width !== 'number' && typeof maxWidth === 'number') skelW = maxWidth;
    if (typeof width !== 'number' && typeof maxWidth === 'string' && maxWidth.endsWith('px')) skelW = parseInt(maxWidth, 10);
    if (typeof maxWidth === 'number' && skelW > maxWidth) skelW = maxWidth;
    if (typeof maxWidth === 'string' && maxWidth.endsWith('px')) {
        const mw = parseInt(maxWidth, 10);
        if (skelW > mw) skelW = mw;
    }

    const showImage = !loading && url && imgLoaded && !imgError;

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                ...wrapperSx
            }}
        >
            {/* Skeleton placeholder — shown only when parent hasn't suppressed it */}
            {!showImage && !hideLoader && (
                <Skeleton
                    variant={isBig ? 'rounded' : 'circular'}
                    animation="wave"
                    width={isBig ? skelW : skelH}
                    height={skelH}
                    sx={{ bgcolor: 'grey.200', flexShrink: 0, borderRadius: isBig ? '4px' : '50%', overflow: 'hidden', maxWidth: maxWidth != null ? maxWidth : '100%' }}
                />
            )}

            {/*
        The <img> is ALWAYS in the DOM (when url exists) so onLoad can fire.
        We use visibility:hidden + position:absolute while loading
        (display:none can skip onLoad for cached images in some browsers).
      */}
            {url && !imgError && (
                <img
                    ref={imgRefCallback}
                    src={url}
                    alt={name}
                    onLoad={() => { setImgLoaded(true); onLoadProp?.(); }}
                    onError={() => setImgError(true)}
                    style={{
                        width: width != null ? width : 'auto',
                        height: height != null ? height : 'auto',
                        maxWidth: maxWidth != null ? maxWidth : '100%',
                        maxHeight: maxHeight != null ? maxHeight : '100%',
                        objectFit: 'contain',
                        flexShrink: 0,
                        ...(showImage
                            ? {}
                            : { position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }
                        ),
                    }}
                    {...props}
                />
            )}
        </Box>
    );
};

BrandedLogo.propTypes = {
    name: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(['big', 'small']),
    sx: PropTypes.object,
    hideLoader: PropTypes.bool,
    onLoad: PropTypes.func,
};

export default BrandedLogo;
